/**
 * Small interactive pieces for Kunnie sections.
 *
 * <kunnie-tabs>: tablist with [role="tab"] buttons controlling [role="tabpanel"]
 *   elements. Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern).
 * <kunnie-carousel>: horizontal scroll-snap track ([data-track]) with optional
 *   [data-prev] / [data-next] buttons that scroll by one viewport of cards.
 * <kunnie-slider>: scroll-snap track ([data-track]) with generated dot buttons in
 *   [data-dots]; the dot of the most visible slide is marked aria-current. Dots
 *   only appear while the track actually scrolls (e.g. the mobile hero).
 */

class KunnieTabs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    for (const tab of this.#tabs) {
      tab.addEventListener('click', () => this.select(tab), { signal });
      tab.addEventListener('keydown', this.#onKeydown, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {HTMLButtonElement} tab */
  select(tab, focus = false) {
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    const rtl = getComputedStyle(this).direction === 'rtl';
    const targets = {
      ArrowRight: rtl ? index - 1 : index + 1,
      ArrowLeft: rtl ? index + 1 : index - 1,
      Home: 0,
      End: tabs.length - 1,
    };
    if (!(event.key in targets)) return;
    event.preventDefault();
    const next = tabs[(targets[/** @type {keyof typeof targets} */ (event.key)] + tabs.length) % tabs.length];
    if (next) this.select(next, true);
  };
}

class KunnieCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#scroll(-1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#scroll(1), { signal });
    this.#track?.addEventListener('scroll', this.#updateButtons, { signal, passive: true });
    this.#updateButtons();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.#track;
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * rtl * track.clientWidth * 0.9, behavior: reduced ? 'auto' : 'smooth' });
  }

  #updateButtons = () => {
    const track = this.#track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    const position = Math.abs(track.scrollLeft);
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = position <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = position >= max;
  };
}

class KunnieSlider extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const track = this.#track;
    const dots = this.querySelector('[data-dots]');
    if (!track || !dots) return;

    const slides = /** @type {HTMLElement[]} */ ([...track.children]);
    const label = dots.getAttribute('aria-label') || '';
    dots.replaceChildren(
      ...slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
        dot.addEventListener('click', () => this.#goTo(slide), { signal: this.#controller?.signal });
        return dot;
      })
    );
    dots.setAttribute('aria-label', label);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio < 0.6) continue;
          const index = slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        }
      },
      { root: track, threshold: [0.6] }
    );
    slides.forEach((slide) => this.#observer?.observe(slide));
    dots.children[0]?.setAttribute('aria-current', 'true');

    // Hide the dots whenever the track does not scroll (desktop grid layout).
    this.#resizeObserver = new ResizeObserver(() => {
      /** @type {HTMLElement} */ (dots).hidden = track.scrollWidth <= track.clientWidth + 2;
    });
    this.#resizeObserver.observe(track);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  /** @param {HTMLElement} slide */
  #goTo(slide) {
    const track = this.#track;
    if (!track) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
    track.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('kunnie-tabs')) customElements.define('kunnie-tabs', KunnieTabs);
if (!customElements.get('kunnie-carousel')) customElements.define('kunnie-carousel', KunnieCarousel);
if (!customElements.get('kunnie-slider')) customElements.define('kunnie-slider', KunnieSlider);
